/**
 * Products tab component
 * Features:
 * - List view with expandable product cards
 * - Treemap view for portfolio visualization
 * - Toggle between views
 */

import { useState, useMemo } from 'react';
import { useCompanyProducts, useCompanyPortfolio } from '../../../hooks/useCompanies';
import { ProductDetailModal } from '../ProductDetailModal';
import { ProductTreemap, type TreemapProduct } from '../ProductTreemap';
import { ProductTreeView } from '../ProductTreeView';
import { PortfolioView } from '../PortfolioView';
import { VerificationBadge } from '../../common/VerificationBadge';
import type {
  Product,
  Capability,
  CapabilityStatus,
  ValueChainPosition,
  VerificationStatus,
  SynthesizedProductAnalysis,
  SynthesizedProduct,
} from '../../../types';

type ViewMode = 'portfolio' | 'tree' | 'list' | 'treemap';
type TreemapMetric = 'revenue' | 'users' | 'marketShare';

interface ProductsTabProps {
  companyId: string;
  synthesisProducts?: SynthesizedProductAnalysis;
}

const capabilityStatusColors: Record<CapabilityStatus, { bg: string; text: string }> = {
  mature: { bg: 'bg-green-100', text: 'text-green-800' },
  in_development: { bg: 'bg-blue-100', text: 'text-blue-800' },
  acquired_not_integrated: { bg: 'bg-purple-100', text: 'text-purple-800' },
  beta: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
  deprecated: { bg: 'bg-gray-100', text: 'text-gray-600' },
};

const capabilityStatusLabels: Record<CapabilityStatus, string> = {
  mature: 'Mature',
  in_development: 'In Development',
  acquired_not_integrated: 'Acquired',
  beta: 'Beta',
  deprecated: 'Deprecated',
};

const valueChainLabels: Record<ValueChainPosition, string> = {
  upstream: 'Upstream',
  midstream: 'Midstream',
  downstream: 'Downstream',
  platform: 'Platform',
  infrastructure: 'Infrastructure',
};

function CapabilityBadge({ capability }: { capability: Capability }) {
  const colors = capabilityStatusColors[capability.status];
  return (
    <span
      className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${colors.bg} ${colors.text}`}
    >
      {capability.name}
      <span className="ml-1 text-[10px] opacity-75">
        ({capabilityStatusLabels[capability.status]})
      </span>
    </span>
  );
}

function ProductCard({
  product,
  isExpanded,
  onToggle,
  onViewDetails,
}: {
  product: Product;
  isExpanded: boolean;
  onToggle: () => void;
  onViewDetails: () => void;
}) {
  const contentId = `product-content-${product.id}`;

  return (
    <div
      className="rounded-lg border border-gray-200 bg-white overflow-hidden"
      data-testid="product-card"
    >
      <button
        onClick={onToggle}
        aria-expanded={isExpanded}
        aria-controls={contentId}
        className="w-full px-4 py-4 text-left hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-inset transition-colors"
      >
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-base font-medium text-gray-900">{product.name}</h3>
              {product.is_flagship && (
                <span className="inline-flex items-center rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700">
                  Flagship
                </span>
              )}
              {product.verification_status && product.verification_status !== 'unverified' && (
                <VerificationBadge
                  status={product.verification_status as VerificationStatus}
                  showLabel={false}
                  size="sm"
                />
              )}
            </div>
            {product.category && <p className="mt-1 text-sm text-gray-500">{product.category}</p>}
          </div>
          <svg
            className={`h-5 w-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {isExpanded && (
        <div id={contentId} className="px-4 pb-4 border-t border-gray-100">
          {product.description && (
            <p className="mt-3 text-sm text-gray-600">{product.description}</p>
          )}

          {/* Value chain & target customers */}
          <div className="mt-4 flex flex-wrap gap-2">
            {product.value_chain_position && (
              <span className="inline-flex items-center rounded bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
                {valueChainLabels[product.value_chain_position]}
              </span>
            )}
            {product.target_customers?.map((customer) => (
              <span
                key={customer}
                className="inline-flex items-center rounded bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700"
              >
                {customer.replace('_', ' ')}
              </span>
            ))}
          </div>

          {/* Capabilities */}
          {product.capabilities && product.capabilities.length > 0 && (
            <div className="mt-4">
              <h4 className="text-xs font-medium text-gray-700 mb-2">Capabilities</h4>
              <div className="flex flex-wrap gap-2">
                {product.capabilities.map((cap, i) => (
                  <CapabilityBadge key={i} capability={cap} />
                ))}
              </div>
            </div>
          )}

          {/* Competitive notes */}
          {product.competitive_notes && product.competitive_notes.length > 0 && (
            <div className="mt-4 border-t border-gray-100 pt-4">
              <h4 className="text-xs font-medium text-gray-700 mb-2">Competitive Notes</h4>
              <ul className="space-y-2">
                {product.competitive_notes.map((note, i) => (
                  <li key={i} className="text-sm text-gray-600">
                    <span className="italic">&quot;{note.content}&quot;</span>
                    <span className="ml-2 text-xs text-gray-400">— {note.source_name}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* View Details button */}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onViewDetails();
              }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
            >
              View Full Profile
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SynthesisProductCard({ product }: { product: SynthesizedProduct }) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="rounded-lg border border-purple-200 bg-purple-50/30 overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 text-left hover:bg-purple-50/50 transition-colors"
      >
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="text-sm font-medium text-gray-900">{product.name}</h4>
              {product.category && (
                <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-medium text-purple-700">
                  {product.category}
                </span>
              )}
            </div>
            {product.description && (
              <p className="mt-1 text-xs text-gray-600 line-clamp-2">{product.description}</p>
            )}
          </div>
          <svg
            className={`h-4 w-4 text-gray-400 transition-transform flex-shrink-0 ml-2 ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 border-t border-purple-200/50 space-y-3">
          {/* Key Features */}
          {product.key_features && product.key_features.length > 0 && (
            <div className="mt-3">
              <h5 className="text-xs font-medium text-gray-700 mb-1">Key Features</h5>
              <ul className="space-y-1">
                {product.key_features.map((f, i) => (
                  <li key={i} className="flex items-start text-xs text-gray-600">
                    <span className="text-purple-500 mr-1.5 mt-0.5">&#8226;</span>
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Pricing */}
          {product.pricing_model && (
            <div>
              <h5 className="text-xs font-medium text-gray-700 mb-1">Pricing</h5>
              <p className="text-xs text-gray-600">{product.pricing_model}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            {/* Target Customers */}
            {product.target_customers && product.target_customers.length > 0 && (
              <div>
                <h5 className="text-xs font-medium text-gray-700 mb-1">Target Customers</h5>
                <div className="flex flex-wrap gap-1">
                  {product.target_customers.map((c, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center rounded bg-blue-50 px-1.5 py-0.5 text-[10px] text-blue-700"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Competitors */}
            {product.competitors && product.competitors.length > 0 && (
              <div>
                <h5 className="text-xs font-medium text-gray-700 mb-1">Competitors</h5>
                <div className="flex flex-wrap gap-1">
                  {product.competitors.map((c, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center rounded bg-red-50 px-1.5 py-0.5 text-[10px] text-red-700"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Strengths & Weaknesses */}
          <div className="grid grid-cols-2 gap-3">
            {product.strengths && product.strengths.length > 0 && (
              <div>
                <h5 className="text-xs font-medium text-gray-700 mb-1">Strengths</h5>
                <ul className="space-y-0.5">
                  {product.strengths.map((s, i) => (
                    <li key={i} className="flex items-start text-xs text-gray-600">
                      <span className="text-green-500 mr-1">+</span>
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {product.weaknesses && product.weaknesses.length > 0 && (
              <div>
                <h5 className="text-xs font-medium text-gray-700 mb-1">Weaknesses</h5>
                <ul className="space-y-0.5">
                  {product.weaknesses.map((w, i) => (
                    <li key={i} className="flex items-start text-xs text-gray-600">
                      <span className="text-red-500 mr-1">-</span>
                      {w}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SynthesisProductsPanel({ analysis }: { analysis: SynthesizedProductAnalysis }) {
  if (!analysis.products || analysis.products.length === 0) return null;

  return (
    <div className="rounded-lg border border-purple-200 bg-white p-5">
      <div className="flex items-center gap-2 mb-4">
        <h3 className="text-sm font-semibold text-gray-900">AI Product Analysis</h3>
        <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-medium text-purple-700">
          Synthesized
        </span>
      </div>

      {/* Portfolio Assessment */}
      {analysis.portfolio_assessment && (
        <p className="text-sm text-gray-600 mb-4">{analysis.portfolio_assessment}</p>
      )}

      {/* Synthesized Product Cards */}
      <div className="space-y-2">
        {analysis.products.map((product, i) => (
          <SynthesisProductCard key={i} product={product} />
        ))}
      </div>

      <div className="mt-3 text-xs text-gray-400 text-right">
        Confidence: {Math.round(analysis.confidence * 100)}%
      </div>
    </div>
  );
}

export function ProductsTab({ companyId, synthesisProducts }: ProductsTabProps) {
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set());
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('portfolio'); // Default to portfolio view
  const [treemapMetric, setTreemapMetric] = useState<TreemapMetric>('revenue');

  const { data: taxonomy, isLoading, error } = useCompanyProducts(companyId);
  const { data: portfolio, isLoading: portfolioLoading } = useCompanyPortfolio(companyId);

  // Transform ProductTaxonomy to TreemapProduct array
  const treemapProducts: TreemapProduct[] = useMemo(() => {
    if (!taxonomy?.segments) return [];

    return taxonomy.segments.flatMap((segment) =>
      segment.products.map((product) => ({
        id: product.id,
        name: product.name,
        category: product.category || segment.name,
        segment: segment.name,
        revenue: null,
        users: null,
        marketShare: null,
        isEstimated: false,
        confidence: 'low' as const,
        revenueModel: undefined,
        isFlagship: product.is_flagship,
      }))
    );
  }, [taxonomy]);

  // Create a map of all products by ID for portfolio view
  const allProductsMap = useMemo(() => {
    if (!taxonomy?.segments) return {};
    const map: Record<string, Product> = {};
    taxonomy.segments.forEach((segment) => {
      segment.products.forEach((product) => {
        map[product.id] = product;
      });
    });
    return map;
  }, [taxonomy]);

  const toggleProduct = (productId: string) => {
    setExpandedProducts((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  };

  const handleViewDetails = (productId: string) => {
    setSelectedProductId(productId);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        {[1, 2].map((i) => (
          <div key={i} className="animate-pulse">
            <div className="h-6 w-48 bg-gray-200 rounded mb-4"></div>
            <div className="space-y-3">
              <div className="h-20 bg-gray-200 rounded-lg"></div>
              <div className="h-20 bg-gray-200 rounded-lg"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <h3 className="text-sm font-medium text-red-800">Error loading products</h3>
        <div className="mt-2 text-sm text-red-700">
          {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      </div>
    );
  }

  const segments = taxonomy?.segments || [];

  return (
    <div className="space-y-6" data-testid="products-tab">
      {/* AI Synthesis Panel */}
      {synthesisProducts && <SynthesisProductsPanel analysis={synthesisProducts} />}

      {/* Header with view toggle */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          {taxonomy?.total_products || 0} products across {segments.length} segments
        </p>

        {/* View toggle */}
        <div className="flex items-center gap-2" role="group" aria-label="View mode">
          <span className="text-xs text-gray-500">View:</span>
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('portfolio')}
              aria-pressed={viewMode === 'portfolio'}
              data-testid="view-toggle-portfolio"
              className={`px-3 py-1 text-sm rounded-md transition-colors flex items-center gap-1.5 ${
                viewMode === 'portfolio'
                  ? 'bg-white text-gray-900 font-medium shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              title="Portfolio with subsidiaries"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
              Portfolio
            </button>
            <button
              onClick={() => setViewMode('tree')}
              aria-pressed={viewMode === 'tree'}
              data-testid="view-toggle-tree"
              className={`px-3 py-1 text-sm rounded-md transition-colors flex items-center gap-1.5 ${
                viewMode === 'tree'
                  ? 'bg-white text-gray-900 font-medium shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              title="Hierarchical tree view"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                />
              </svg>
              Tree
            </button>
            <button
              onClick={() => setViewMode('list')}
              aria-pressed={viewMode === 'list'}
              data-testid="view-toggle-list"
              className={`px-3 py-1 text-sm rounded-md transition-colors flex items-center gap-1.5 ${
                viewMode === 'list'
                  ? 'bg-white text-gray-900 font-medium shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              title="Flat list view"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 10h16M4 14h16M4 18h16"
                />
              </svg>
              List
            </button>
            <button
              onClick={() => setViewMode('treemap')}
              aria-pressed={viewMode === 'treemap'}
              data-testid="view-toggle-treemap"
              className={`px-3 py-1 text-sm rounded-md transition-colors flex items-center gap-1.5 ${
                viewMode === 'treemap'
                  ? 'bg-white text-gray-900 font-medium shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              title="Size-based treemap"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z"
                />
              </svg>
              Treemap
            </button>
          </div>
        </div>
      </div>

      {/* Portfolio View (default) */}
      {viewMode === 'portfolio' && (
        <PortfolioView
          companyName={taxonomy?.company_name || ''}
          directProducts={(portfolio?.direct_products ?? [])
            .map((id) => allProductsMap[id])
            .filter((p): p is Product => p !== undefined)}
          subsidiaries={portfolio?.subsidiaries ?? []}
          allProducts={allProductsMap}
          onProductSelect={(product) => handleViewDetails(product.id)}
          isLoading={portfolioLoading}
        />
      )}

      {/* Tree View */}
      {viewMode === 'tree' &&
        (segments.length > 0 ? (
          <ProductTreeView
            segments={segments}
            onProductSelect={(product) => handleViewDetails(product.id)}
          />
        ) : (
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
              />
            </svg>
            <p className="mt-4 text-gray-500">No products found.</p>
            <p className="text-sm text-gray-400 mt-2">Product data is being collected.</p>
          </div>
        ))}

      {/* Treemap View */}
      {viewMode === 'treemap' && (
        <ProductTreemap
          products={treemapProducts}
          selectedMetric={treemapMetric}
          onMetricChange={setTreemapMetric}
          isLoading={isLoading}
        />
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <div className="space-y-8">
          {/* Segments */}
          {segments.map((segment) => (
            <section key={segment.name}>
              <h2 className="text-lg font-semibold text-gray-900 mb-1">{segment.name}</h2>
              {segment.description && (
                <p className="text-sm text-gray-500 mb-4">{segment.description}</p>
              )}
              <div className="space-y-3">
                {segment.products.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    isExpanded={expandedProducts.has(product.id)}
                    onToggle={() => toggleProduct(product.id)}
                    onViewDetails={() => handleViewDetails(product.id)}
                  />
                ))}
              </div>
            </section>
          ))}

          {/* Empty state */}
          {segments.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">No products found.</p>
              <p className="text-sm text-gray-400 mt-2">Product data is being collected.</p>
            </div>
          )}
        </div>
      )}

      {/* Product Detail Modal */}
      <ProductDetailModal
        companyId={companyId}
        productId={selectedProductId}
        onClose={() => setSelectedProductId(null)}
      />
    </div>
  );
}

export default ProductsTab;
